import { range } from 'd3-array';
import { UAParser } from 'ua-parser-js';
import { getYear, getMonth } from 'date-fns';
import 'dayjs/locale/ko'
import dayjs from 'dayjs';

dayjs.locale('ko');

/**
 * todo 년도 구하기
 * @param {number} start todo 시작년도
 * @param {number} end todo 종료년도
 * @param {string} format todo 날짜 포맷
 */
export const getYearOption = (start?: number, end?: number, format?: string) => {
	let years: string[] | number[] = range(start || getYear(new Date()), end || getYear(new Date()) + 10, 1);
	
	switch (format) {
		case 'YYYY년':
			return years.map(year => `${year}년`);
		default:
			return years;
	}
};

// ISOString > Date 변환하는 헬퍼 함수
export const parseDate = (
	isoString: string,
	options?: {
		addDays?: number;
		minusDays?: number;
	}
): Date | null => {
	if (!isoString) return null;
	
	const date = new Date(isoString);

	// 옵션 객체가 넘어왔을 때만 실행
	if (options) {
		const { addDays, minusDays } = options;
		
		if (addDays !== undefined) {
			date.setDate(date.getDate() + addDays);
		}
		if (minusDays !== undefined) {
			date.setDate(date.getDate() - minusDays);
		}
	}
	
	return date;
};

// 날짜 포맷팅 함수
export const formatDate = (date?: string, format = 'YYYY-MM-DD HH:mm') => {
	if (!date) return '-';
	return dayjs(date).format(format);
};
export const formatDateRange = (startDate?: string, endDate?: string, format = 'YYYY.MM.DD(ddd) HH:mm') => {
	if (!startDate || !endDate) return '-';
	return `${dayjs(startDate).format(format)} ~ ${dayjs(endDate).format(format)}`;
};
// 24시간 1시간 단위
export const getHourOptions = () => {
	const hourOptions = [];
	
	for (let hours = 0; hours <= 23; hours++) {
		hourOptions.push({
			title: `${String(hours).padStart(2, '0')}시`,
			value: hours
		});
	}
	return hourOptions;
};
// 15분 단위
export const getMinOptions = () => {
	const minOptions = [];
	
	for (let min = 0; min < 60; min += 10) {
		minOptions.push({
			title: `${String(min).padStart(2, '0')}분`,
			value: min
		});
	}
	return minOptions;
};
// 날짜에서 시간/분 추출 함수
export const extractHourMin = (dateString?: string) => {
	if (!dateString) return { hour: 0, min: 0 };
	const date = new Date(dateString);
	return { hour: date.getHours(), min: date.getMinutes() };
};

// 국가 코드 (ISO 변경)
export const getCountryName = (countryCode: string) => {
	try {
		const regionNames = new Intl.DisplayNames(['ko'], { type: 'region' });
		return regionNames.of(countryCode.toUpperCase());
	} catch (err) {
		return countryCode;
	}
}

// 날짜에 시간/분 설정
export const setDateTime = (date: Date, hour: number, minute: number): Date => {
	const newDate = new Date(date);
	newDate.setHours(hour);
	newDate.setMinutes(minute);
	newDate.setSeconds(0);
	newDate.setMilliseconds(0);
	return newDate;
};

// 파일 이름으로 다운로드
export const downloadFile = async (url: string, fileName?: string) => {
	if (!url) return;
	try {
		const response = await fetch(url);
		
		// 파일 데이터를 Blob 형태로 로드
		const blob = await response.blob();
		
		// 브라우저 메모리에 해당 Blob을 가리키는 임시 URL을 생성
		const downloadUrl = window.URL.createObjectURL(blob);
		
		// 가상의 <a> 태그를 생성하여 클릭 이벤트 발생
		const link = document.createElement('a');
		const extension = url.split('.').pop(); // url 끝에서 확장자 추출
		link.href = downloadUrl;
		
		link.download = fileName ?? `rockandrun_download.${extension}`;
		
		document.body.appendChild(link);
		link.click();
		
		// 다운로드 후 임시 요소와 URL 정리
		document.body.removeChild(link);
		window.URL.revokeObjectURL(downloadUrl);
	} catch (error) {
		console.error('파일 다운로드 중 오류 발생:', error);
	}
};

// 사용자 ID 6자리 변환
export const formatUserId = (userId: number | string) => {
	const stringUserId = userId.toString() || '-';
	
	if (stringUserId.length < 6) {
		return stringUserId.padStart(6, '0');
	}
	return stringUserId;
}

// 영문 대소문자 포함 여부
export const isRequiredChars = (password: string) => {
	let reg = /(?=.*?[a-z])(?=.*?[A-Z])/;
	return reg.test(password);
};

// 숫자 특수문자 포함 여부
export const isNumericSpecialChars = (password: string) => {
	let reg = /(?=.*?[0-9])(?=.*?[#?!@$%^&*-])/;
	return reg.test(password);
};

// 문자열 길이 8~16자리 체크 여부
export const isRequiredLength = (password: string) => {
	let reg = /^.{8,16}$/;
	return reg.test(password);
};

// 반복되는 문자 체크 여부 - 반복되면 true
export const isRepeatedChars = (password: string) => {
	// let reg = /(.)\1+/;
	let reg = /(.)\1{2,}/;
	
	return reg.test(password);
};

/**
 * 복잡한 UserAgent 문자열을 'Browser (OS)' 형태로 직관적으로 요약
 * @example "Mozilla/... Chrome/148..." -> "Chrome 148 (macOS)"
 * @example "Mozilla/... iPhone... KAKAO..." -> "KakaoTalk (iOS / iPhone)"
 */
export function getUASummary(userAgentStr: string | undefined): string {
	if (!userAgentStr) return '';
	
	const parser = new UAParser(userAgentStr);
	const result = parser.getResult();
	
	// 브라우저 이름 및 메인 버전 (예: Chrome 148, Whale 3)
	const browserName = result.browser.name || 'Unknown Browser';
	const browserVersion = result.browser.version ? result.browser.version.split('.')[0] : '';
	const browserInfo = browserVersion ? `${browserName} ${browserVersion}` : browserName;
	
	// OS 이름 (예: Windows, macOS, iOS, Android)
	let osInfo = result.os.name || 'Unknown OS';
	
	// 모바일/태블릿일 경우 디바이스 모델명 추가 기재 (예: iPhone, Galaxy S24 등)
	// PC 환경일 때는 result.device.model이 undefined이므로 생략
	const deviceModel = result.device.model || result.device.vendor;
	const environmentInfo = deviceModel ? `${osInfo} / ${deviceModel}` : osInfo;
	
	// 최종 결합 형태 리턴
	return `${browserInfo} (${environmentInfo})`;
}
